import { getConfig, getEndpoints, getSupabaseClient } from './config.js';
import { getAuthToken } from './auth.js';

/**
 * Lightswitch SDK Core Paywall
 * 
 * Handles paywall display and usage logging functionality.
 * Pure JavaScript implementation with no framework dependencies.
 */


// Paywall state
let currentPaywall = null;
const paywallListeners = new Set();

/**
 * Get Supabase access token if available
 * @returns {string|null} Supabase access token or null
 */
const getSupabaseToken = async () => {
  try {
    const supabase = getSupabaseClient();
    if (!supabase) {
      console.warn('Supabase client not available for paywall token');
      return null;
    }
    
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error getting Supabase session for paywall:', error);
      return null;
    }
    
    return session?.access_token || null;
  } catch (error) {
    console.error('Error accessing Supabase token for paywall:', error);
    return null;
  }
};

/**
 * Notify all paywall listeners
 * @param {string} event - Event type
 * @param {any} data - Event data
 */
const notifyPaywallListeners = (event, data) => {
  paywallListeners.forEach(listener => {
    try {
      listener(event, data);
    } catch (error) {
      console.error('Error in paywall listener:', error);
    }
  });
};

/**
 * Add listener for paywall events
 * @param {Function} listener - Callback function that receives (event, data)
 * @returns {Function} Unsubscribe function
 */
const onPaywallEvent = listener => {
  paywallListeners.add(listener);

  // Return unsubscribe function
  return () => {
    paywallListeners.delete(listener);
  };
};

/**
 * Create paywall URL with parameters
 * @param {string} [paywallConfigId] - Paywall configuration ID
 * @param {string} [token] - Auth token
 * @returns {string} Complete paywall URL
 */
const createPaywallUrl = (paywallConfigId, token) => {
  getConfig();
  const endpoints = getEndpoints();
  console.log(`Creating paywall URL with paywallConfigId:`, paywallConfigId);
  let paywallUrl = endpoints.paywall;
  const params = new URLSearchParams();
  if (paywallConfigId) {
    params.append('paywall_config_id', paywallConfigId);
    console.log(`Added paywall_config_id to URL params:`, paywallConfigId);
  } else {
    console.log(`No paywallConfigId provided - URL will not include paywall_config_id parameter`);
  }
  if (token) {
    params.append('token', token);
  }

  // Only append the query string if we have parameters
  if (params.toString()) {
    paywallUrl += `?${params.toString()}`;
  }
  console.log(`Final paywall URL:`, paywallUrl);
  return paywallUrl;
};

/**
 * Create and show a paywall modal
 * @param {Object} options - Paywall options
 * @param {string} [options.feature] - Feature that triggered the paywall
 * @param {string} [options.paywallConfigId] - Paywall configuration ID
 * @param {Function} [options.onClose] - Callback when paywall is closed
 * @param {Function} [options.onPaymentSuccess] - Callback when payment succeeds
 * @returns {Promise<Object>} Paywall control object
 */
const showPaywall = async function () {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (typeof window === 'undefined') {
    console.error('Paywall only available in browser environment');
    return null;
  }
  const {
    feature,
    paywallConfigId,
    onClose,
    onPaymentSuccess
  } = options;

  // Close existing paywall if any
  if (currentPaywall) {
    currentPaywall.close();
  }
  
  // Try to get Supabase token first, fallback to Lightswitch token
  let authToken = await getSupabaseToken();
  if (!authToken) {
    console.log('No Supabase token available, using Lightswitch token');
    authToken = getAuthToken();
  } else {
    console.log('Using Supabase access token for paywall');
  }
  
  const paywallUrl = createPaywallUrl(paywallConfigId, authToken);

  // Create modal container
  const modal = document.createElement('div');
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(4px);
    z-index: 10000;
    display: flex;
    flex-direction: column;
  `;

  // Note: Close button removed to prevent users from dismissing paywall without paying

  // Create iframe
  const iframe = document.createElement('iframe');
  iframe.src = paywallUrl;
  iframe.style.cssText = `
    width: 100%;
    height: 100%;
    border: none;
  `;
  iframe.title = 'Upgrade to Pro';
  iframe.allow = 'payment; encrypted-media; publickey-credentials-get; publickey-credentials-create';
  iframe.sandbox = 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox';

  // Assemble modal
  modal.appendChild(iframe);

  // Handle messages from iframe
  const handleMessage = event => {
    const data = event.data;
    if (data.type === 'payment_success') {
      console.log('Payment successful');
      notifyPaywallListeners('payment_success', {
        feature,
        data
      });
      if (onPaymentSuccess) {
        onPaymentSuccess(data);
      }
      closePaywall();
    }
    if (data.type === 'url_change') {
      console.log('Iframe URL changed:', data.url);
      notifyPaywallListeners('url_change', {
        feature,
        url: data.url
      });
    }
  };

  // Note: ESC key and backdrop click handlers removed to prevent dismissing paywall without paying

  // Close function
  const closePaywall = () => {
    if (modal.parentNode) {
      modal.parentNode.removeChild(modal);
    }
    window.removeEventListener('message', handleMessage);
    document.body.style.overflow = '';
    currentPaywall = null;
    notifyPaywallListeners('close', {
      feature
    });
    if (onClose) {
      onClose();
    }
  };

  // Add event listeners
  window.addEventListener('message', handleMessage);

  // Prevent body scrolling
  document.body.style.overflow = 'hidden';

  // Add to DOM
  document.body.appendChild(modal);

  // Store current paywall reference
  currentPaywall = {
    modal,
    iframe,
    close: closePaywall,
    feature
  };
  notifyPaywallListeners('open', {
    feature,
    paywallConfigId
  });
  return currentPaywall;
};

/**
 * Close the current paywall if open
 */
const closePaywall = () => {
  if (currentPaywall) {
    currentPaywall.close();
  }
};

/**
 * Check if a paywall is currently open
 * @returns {boolean} True if paywall is open
 */
const isPaywallOpen = () => {
  return currentPaywall !== null;
};

/**
 * Get current paywall info
 * @returns {Object|null} Current paywall info or null
 */
const getCurrentPaywall = () => {
  return currentPaywall ? {
    feature: currentPaywall.feature,
    isOpen: true
  } : null;
};

export { closePaywall, createPaywallUrl, getCurrentPaywall, isPaywallOpen, onPaywallEvent, showPaywall };
//# sourceMappingURL=paywall.js.map
